import { useParams, Link } from "react-router-dom";
import { getArticleBySlug } from "@/mocks/articles";
import { articles } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");

  if (!article) {
    return (
      <div className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:no-underline hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-line"></i>返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: article.title,
                description: article.excerpt,
                author: { "@type": "Organization", name: article.author },
                datePublished: article.publishDate,
                image: article.coverImage,
                url: `https://naipianku.com/article/${article.slug}`,
                publisher: { "@type": "Organization", name: "牛奶片库", url: "https://naipianku.com" },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "首页", item: "https://naipianku.com" },
                  { "@type": "ListItem", position: 2, name: "选购指南", item: `https://naipianku.com/article/milk-tablet-buying-guide-complete` },
                  { "@type": "ListItem", position: 3, name: article.title },
                ],
              },
            ],
          }),
        }}
      />

      <article className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "选购指南", path: "/article/milk-tablet-buying-guide-complete" },
                { label: article.title },
              ]}
            />
          </div>

          <header className="py-8 md:py-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-md bg-primary-100 text-primary-700">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 text-[11px] rounded-md bg-background-100 text-foreground-600 border border-background-200/70">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>{article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>{article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>{article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full object-cover"
              style={{ aspectRatio: "16/9" }}
            />
          </div>

          <div
            className="article-content text-sm md:text-base"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200">
              <h3 className="text-lg font-semibold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-background-300 transition-all duration-300 no-underline hover:no-underline"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{rel.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}